import { useEffect, useRef } from "react";

/**
 * Masked line-by-line reveal. Wrap text in <RevealText as="h1">…</RevealText>
 * and pass either a string (auto-split by spaces into words) or an array of
 * pre-split lines for fine control.
 *
 * Uses IntersectionObserver, not framer-motion, so it works even before
 * hydration and stays cheap.
 */
export default function RevealText({
  as: Tag = "h2",
  lines,
  children,
  className = "",
  delay = 0,
  stagger = 90,
  once = true,
}) {
  const ref = useRef(null);

  const resolvedLines = Array.isArray(lines)
    ? lines
    : typeof children === "string"
      ? [children]
      : [children];

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const targets = el.querySelectorAll(".mask-line");

    const reveal = () => {
      targets.forEach((t, i) => {
        setTimeout(() => t.classList.add("is-revealed"), delay + i * stagger);
      });
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            reveal();
            if (once) io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [delay, stagger, once]);

  return (
    <Tag ref={ref} className={className}>
      {resolvedLines.map((line, i) => (
        <span className="mask-line" key={i}>
          <span>{line}</span>
        </span>
      ))}
    </Tag>
  );
}
